import { useToast } from "@chakra-ui/react";
import { type FC, useCallback, useEffect, useState } from "react";

import { dayjs } from "@equidefi/i18n";
import { DROPZONE_ACCEPTED_TYPES, ENTITY, JOINT } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import { Text } from "@equidefi/ui";

import { Dropzone, type FileRejection } from "components/Dropzone";
import { useHandleAccreditationUpload, useStoreAccreditationFile } from "hooks/useAccreditation";
import { useSavedProfilePresignedUrl, useStoreSavedProfileFile } from "hooks/useSavedProfiles";
import { AccreditationFileList } from "./AccreditationFileList";
import { AccreditationMethodSection } from "./AccreditationMethodSection";
import { AccreditationRequirements } from "./AccreditationRequirements";
import { DEFAULT_MAX_SIZE, handleRejectionFiles } from "./helpers";

type UploadAreaProps = {
  vault: DTO.Vault;
  isTerminated?: boolean;
  refetchFiles: () => void;
  investment?: DTO.Investment;
  accreditationFiles: DTO.AccreditationFile[];
  savedProfileId?: string | null;
};

const MAX_FILES = 5;

const UploadArea: FC<UploadAreaProps> = ({
  vault,
  isTerminated,
  refetchFiles,
  investment,
  accreditationFiles,
  savedProfileId,
}) => {
  const toast = useToast();
  const year = dayjs().year();
  const isJoint = vault?.ta_account_type === JOINT;
  const isEntity = vault?.entity_type === ENTITY;
  const [type, setType] = useState<"INCOME_VERIFICATION" | "PRO_LETTER" | "ASSET_VERIFICATION" | null>(null);
  const [files, setFiles] = useState<DTO.AccreditationFile[]>(() => accreditationFiles || []);

  const storeVaultFile = useStoreAccreditationFile(investment?.id as string);
  const storeProfileFile = useStoreSavedProfileFile(savedProfileId ?? "");
  const storeFile = savedProfileId ? storeProfileFile : storeVaultFile;

  const profilePresignedUrl = useSavedProfilePresignedUrl(savedProfileId ?? "");

  const accreditationUpload = useHandleAccreditationUpload({
    investmentId: investment?.id as string,
    presignedUrl: savedProfileId ? profilePresignedUrl : undefined,
    storeFile,
    onError: (error) => {
      toast({
        status: "error",
        description: error?.message || "Sorry, the document upload failed.",
      });
    },
  });
  const handleOnDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length === 0 && rejectedFiles.length === 0) {
        toast({
          id: "accreditation-upload-empty",
          status: "error",
          description: "We didn't receive your file. Please tap the upload area and try again.",
        });
        return;
      }

      const rejected = handleRejectionFiles(rejectedFiles, {
        maxFiles: MAX_FILES,
        onReject: (message) => {
          toast({
            status: "error",
            description: message,
          });
        },
      });
      if (rejected) return;

      for (const accepted of acceptedFiles) {
        const file = await accreditationUpload.upload(accepted, type as string);
        if (file) {
          setFiles((prev) => [...prev, file]);
          toast({
            status: "success",
            description: `"${accepted.name}" uploaded successfully.`,
          });
        }
      }
      await refetchFiles();
    },
    [accreditationUpload, type, toast],
  );

  useEffect(() => {
    if (accreditationFiles?.length > 0) {
      setFiles(accreditationFiles);
    }
  }, [accreditationFiles]);

  return (
    <>
      <AccreditationMethodSection
        onSelect={() => setType("PRO_LETTER")}
        title="Accredited Investor Verification Letter"
        description="Upload a professional letter that confirms you meet the accredited investor criteria.">
        <AccreditationRequirements
          summary="Upload a signed verification letter from a qualified professional confirming your accredited investor status."
          requirements={[
            "Issued by a CPA, licensed attorney, SEC-registered investment adviser, or registered broker-dealer.",
            "Dated within the past 60 days.",
            "Identifies you and the specific offering.",
            "Confirms that the professional took reasonable steps to verify your accredited investor status within the preceding three months and determined that you qualify.",
            "Includes the professional's name, credentials, signature, and contact information.",
          ]}
        />
        <Text fontWeight="bold" fontSize="lg" mb={2}>
          Document Upload
        </Text>

        <AccreditationFileList
          noFilesWarning={false}
          vaultId={vault?.id}
          savedProfileId={savedProfileId}
          files={files?.filter((file) => file.type === "PRO_LETTER")}
          onDelete={(id) => {
            setFiles((prev) => prev.filter((f) => f.id !== id));
            refetchFiles();
          }}
          isEditable={false}
          isDeletable={!isTerminated}
          isLoading={accreditationUpload.isUploading}
          mb={3}
        />
        <Dropzone
          accept={DROPZONE_ACCEPTED_TYPES}
          disabled={!type}
          maxSize={DEFAULT_MAX_SIZE}
          maxFiles={MAX_FILES}
          boxProps={{
            cursor: type ? "pointer" : "not-allowed",
            borderRadius: "2xl",
            h: "80px",
            borderColor: "equidefi.darkGray",
            color: "equidefi.darkGray",
          }}
          onDrop={handleOnDrop}
          onFileDialogCancel={() =>
            toast({
              id: "accreditation-upload-empty",
              status: "error",
              description: "We didn't receive your file. Please tap the upload area and try again.",
            })
          }
          // isLoading={accreditationUpload.isUploading}
          responsive={true}
          multiple
        />
      </AccreditationMethodSection>

      <AccreditationMethodSection
        onSelect={() => setType("INCOME_VERIFICATION")}
        title="Income Verification"
        description="Upload tax documents, such as Form 1040, 1099, or W-2 to verify your annual income.">
        <AccreditationRequirements
          summary="Upload IRS tax documents demonstrating qualifying income for each of the previous two calendar years."
          requirements={[
            ...(isEntity
              ? []
              : [
                  isJoint
                    ? `Combined income with your spouse or spousal equivalent exceeding $300,000 in both ${year - 2} and ${year - 1}.`
                    : `Income exceeding $200,000 in both ${year - 2} and ${year - 1}.`,
                ]),
            "Acceptable documentation includes Forms W-2, 1099, Schedule K-1, or Form 1040, as applicable.",
            `You must reasonably expect to meet the same applicable income threshold in ${year}.`,
          ]}
        />
        <Text fontWeight="bold" fontSize="lg" mb={2}>
          Document Upload
        </Text>
        <AccreditationFileList
          noFilesWarning={false}
          vaultId={vault?.id}
          savedProfileId={savedProfileId}
          files={files?.filter((file) => file.type === "INCOME_VERIFICATION")}
          onDelete={(id) => {
            setFiles((prev) => prev.filter((f) => f.id !== id));
            refetchFiles();
          }}
          isEditable={false}
          isDeletable={!isTerminated}
          isLoading={accreditationUpload.isUploading}
          mb={3}
        />
        <Dropzone
          accept={DROPZONE_ACCEPTED_TYPES}
          disabled={!type}
          maxSize={DEFAULT_MAX_SIZE}
          maxFiles={MAX_FILES}
          boxProps={{
            cursor: type ? "pointer" : "not-allowed",
            borderRadius: "2xl",
            h: "80px",
            borderColor: "equidefi.darkGray",
            color: "equidefi.darkGray",
          }}
          onDrop={handleOnDrop}
          onFileDialogCancel={() =>
            toast({
              id: "accreditation-upload-empty",
              status: "error",
              description: "We didn't receive your file. Please tap the upload area and try again.",
            })
          }
          // isLoading={accreditationUpload.isUploading}
          responsive={true}
          multiple
        />
      </AccreditationMethodSection>

      <AccreditationMethodSection
        onSelect={() => setType("ASSET_VERIFICATION")}
        title="Asset Verification"
        description="Upload brokerage or bank statements to verify your assets and net worth.">
        <AccreditationRequirements
          summary={
            isEntity
              ? "Upload financial statements demonstrating the entity's qualifying assets."
              : "Upload financial statements demonstrating that your net worth exceeds $1,000,000, excluding your primary residence."
          }
          requirements={[
            "Upload bank, brokerage, or other qualifying financial statements dated within the past three months.",
            "Statements must identify the account holder, statement date, and account value.",
            "Multiple statements may be combined to demonstrate qualifying assets.",
            "All applicable outstanding liabilities must be considered. Additional documentation, including a recent consumer credit report, may be required.",
          ]}
        />
        <Text fontWeight="bold" fontSize="lg" mb={2}>
          Document Upload
        </Text>
        <AccreditationFileList
          noFilesWarning={false}
          vaultId={vault?.id}
          savedProfileId={savedProfileId}
          files={files?.filter((file) => file.type === "ASSET_VERIFICATION")}
          onDelete={(id) => {
            setFiles((prev) => prev.filter((f) => f.id !== id));
            refetchFiles();
          }}
          isEditable={false}
          isDeletable={!isTerminated}
          isLoading={accreditationUpload.isUploading}
          mb={3}
        />
        <Dropzone
          accept={DROPZONE_ACCEPTED_TYPES}
          disabled={!type}
          maxSize={DEFAULT_MAX_SIZE}
          maxFiles={MAX_FILES}
          boxProps={{
            cursor: type ? "pointer" : "not-allowed",
            borderRadius: "2xl",
            h: "80px",
            borderColor: "equidefi.darkGray",
            color: "equidefi.darkGray",
          }}
          onDrop={handleOnDrop}
          onFileDialogCancel={() =>
            toast({
              id: "accreditation-upload-empty",
              status: "error",
              description: "We didn't receive your file. Please tap the upload area and try again.",
            })
          }
          responsive={true}
          multiple
        />
      </AccreditationMethodSection>
    </>
  );
};

export default UploadArea;
